import { all, put, takeLatest, takeEvery, call, take, select } from 'redux-saga/effects'
import { SubmissionError, setSubmitSucceeded, setSubmitFailed, submit } from 'redux-form'
import { errorHandler } from '../../../core/helpers/errorHelpers'
import { searchCompanies, searchCandidateRoles, fetchWorkHistory, createWorkHistory, updateWorkHistory, deleteWorkHistory } from '../api'
import { anyRecordsMissingInformation, currentlyEditingFormNames, hasWorkHistoryRecords } from '../../selectors/workHistory'
import { getLocation } from '../../selectors/router'
import { nextOnboardingUrl } from '../../helpers/onboardingSteps'

import {
  fetchWorkHistorySuccess,
  createWorkHistorySuccess,
  createWorkHistoryError,
  updateWorkHistorySuccess,
  updateWorkHistoryError,
  deleteWorkHistorySuccess,
  deleteWorkHistoryError,
  searchCompaniesSuccess,
  searchCompaniesError,
  searchRolesSuccess,
  searchRolesError,
  FETCH,
  UPDATE,
  CREATE,
  DELETE,
  SEARCH_COMPANIES,
  SEARCH_ROLES,
  SUBMIT,
} from '../modules/workHistory'
import { updateCandidate } from '../modules/candidate'

const SET_SUBMIT_SUCCEEDED = setSubmitSucceeded().type
const SET_SUBMIT_FAILED = setSubmitFailed().type

function* fetchWorkHistorySaga({ meta }) {
  try {
    const response = yield fetchWorkHistory(meta.candidateId)
    yield put(fetchWorkHistorySuccess(response))
  } catch (error) {}
}

function* createWorkHistorySaga({ meta }) {
  try {
    const response = yield call(createWorkHistory, meta.candidateId, meta.data)
    yield call(meta.resolve)
    yield put(createWorkHistorySuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(createWorkHistoryError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* updateWorkHistorySaga({ meta }) {
  try {
    const response = yield call(updateWorkHistory, meta.id, meta.data)
    yield call(meta.resolve)
    yield put(updateWorkHistorySuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(updateWorkHistoryError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* deleteWorkHistorySaga({ meta }) {
  try {
    // Fake the deletion, then actually delete
    yield put(deleteWorkHistorySuccess(meta.id))
    yield deleteWorkHistory(meta.id)
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(deleteWorkHistoryError(error))
  }
}

function* searchCompaniesSaga({ meta, payload }) {
  try {
    const response = yield searchCompanies(meta.term)
    yield put(searchCompaniesSuccess(payload.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(searchCompaniesError(error))
  }
}

function* searchRolesSaga({ meta, payload }) {
  try {
    const response = yield searchCandidateRoles(meta.term)
    yield put(searchRolesSuccess(payload.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(searchRolesError(error))
  }
}

function* submitWorkHistoryForm(formName) {
  // Submit the form
  yield put(submit(formName))

  // Resolve upon either successful submission or failure of the form
  yield take(action => {
    return (action.type === SET_SUBMIT_SUCCEEDED || action.type === SET_SUBMIT_FAILED) && action.meta.form === formName
  })
}

function* submitWorkHistorySaga({ meta, payload: { data } }) {
  try {
    // Submit work history forms that are currently being edited
    // Await successful submission or failure of each form
    const formNames = yield select(currentlyEditingFormNames)
    yield all(formNames.map(formName => call(submitWorkHistoryForm, formName)))

    const hasRecords = yield select(hasWorkHistoryRecords)
    const missingInformation = yield select(anyRecordsMissingInformation)

    const location = yield select(getLocation)
    const url = nextOnboardingUrl(location.pathname)

    if (!hasRecords && !data.no_work_history) {
      yield call(
        meta.reject,
        new SubmissionError({
          _error: 'Please add at least one work experience record before continuing',
        }),
      )
    } else if (missingInformation) {
      yield call(
        meta.reject,
        new SubmissionError({
          _error: 'Please complete the missing information in your work history records before continuing.',
        }),
      )
    } else if (hasRecords && data.no_work_history) {
      // Override indication of no work experience if work history provided
      yield put(updateCandidate({ no_work_history: false }, url))
    } else {
      yield put(updateCandidate({ no_work_history: data.no_work_history }, url))
    }
  } catch (err) {
    yield call(errorHandler, err)
  }
}

export default function* watchWorkHistorySaga() {
  yield takeLatest(FETCH, fetchWorkHistorySaga)
  yield takeEvery(UPDATE, updateWorkHistorySaga)
  yield takeEvery(CREATE, createWorkHistorySaga)
  yield takeLatest(DELETE, deleteWorkHistorySaga)
  yield takeLatest(SEARCH_COMPANIES, searchCompaniesSaga)
  yield takeLatest(SEARCH_ROLES, searchRolesSaga)
  yield takeLatest(SUBMIT, submitWorkHistorySaga)
}
